import type { SpaceDateRangeValue, SpaceParameterValues } from '@src/components/AppLayout/SpaceParameters';
import { LookbackOption } from '@talos/kyoko';

/**
 * These are the possible filter keys that can be used to control the dealer space parameters.
 */
export const DealerSpaceParameterKey = {
  AllInTiers: 'AllInTiers',
  BasePairs: 'BasePairs',
  Counterparty: 'Counterparty',
  Currencies: 'Currencies',
  DateRange: 'DateRange',
  FeeCurrencies: 'FeeCurrencies',
  FeeModes: 'FeeModes',
  FeeTiers: 'FeeTiers',
  MarketAccounts: 'MarketAccounts',
  Modes: 'Modes',
  OrderID: 'OrderID',
  PricingAggregations: 'PricingAggregations',
  PricingMode: 'PricingMode',
  RFQID: 'RFQID',
  Sides: 'Sides',
  SubAccounts: 'SubAccounts',
  SymbolGroups: 'SymbolGroups',
  Symbols: 'Symbols',
  Users: 'Users',
} as const;

export type DealerSpaceParameterKey = (typeof DealerSpaceParameterKey)[keyof typeof DealerSpaceParameterKey];

export const DEALER_SPACE_PARAMETER_KEYS = Object.values(DealerSpaceParameterKey);

export const DEALER_FILTER_BUILDER_SPACE_PARAMETER_KEYS = DEALER_SPACE_PARAMETER_KEYS.filter(
  key => key !== DealerSpaceParameterKey.DateRange
);

export type DealerFilterBuilderSpaceParameterKey = (typeof DEALER_FILTER_BUILDER_SPACE_PARAMETER_KEYS)[number];

function isDealerFilterBuilderSpaceParameterKey(key: string): key is DealerFilterBuilderSpaceParameterKey {
  return DEALER_FILTER_BUILDER_SPACE_PARAMETER_KEYS.includes(key as DealerFilterBuilderSpaceParameterKey);
}

export function getDealerFilterBuilderSpaceParameterKeys(
  availableKeys: readonly string[]
): DealerFilterBuilderSpaceParameterKey[] {
  const availableKeySet = new Set<string>(availableKeys);
  return DEALER_FILTER_BUILDER_SPACE_PARAMETER_KEYS.filter(key => availableKeySet.has(key));
}

export function getDisplayedDealerFilterBuilderSpaceParameterKeys(
  availableKeys: readonly string[],
  values: SpaceParameterValues
): DealerFilterBuilderSpaceParameterKey[] {
  const selectedKeys = Object.keys(values)
    .filter(isDealerFilterBuilderSpaceParameterKey)
    .filter(key => {
      const value = values[key];
      return Array.isArray(value) && value.length > 0;
    });
  const selectedKeySet = new Set<string>(selectedKeys);

  return [
    ...selectedKeys,
    ...getDealerFilterBuilderSpaceParameterKeys(availableKeys).filter(key => !selectedKeySet.has(key)),
  ];
}

export function hasDealerSpaceDateRange(availableKeys: readonly string[]): boolean {
  return availableKeys.includes(DealerSpaceParameterKey.DateRange);
}

export const DEFAULT_DEALER_SPACE_DATE_RANGE_VALUE = {
  lookback: LookbackOption.Past24Hours,
} as const satisfies SpaceDateRangeValue;
